import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianAxis,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Flex,
  Heading,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

const MonthlyEarningChart = ({ data }) => {
  const noDataBgColor = useColorModeValue("#fff", "#081b2e");
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyTotalAmount, setMonthlyTotalAmount] = useState(0);

  //calculate monthly data
  const calculateMonthlyData = (earningsData) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const monthlyAmounts = {};
    earningsData.forEach((entry) => {
      const date = new Date(entry.created_at);
      const entryMonth = date.getMonth();
      const entryYear = date.getFullYear();
      const dayOfMonth = date.getDate();

      if (entryMonth === currentMonth && entryYear === currentYear) {
        const amount = parseFloat(entry.amount);

        if (!isNaN(amount)) {
          if (monthlyAmounts[dayOfMonth]) {
            monthlyAmounts[dayOfMonth] += amount;
          } else {
            monthlyAmounts[dayOfMonth] = amount;
          }
        }
      }
    });

    const formattedMonthlyData = Array.from(
      { length: lastDayOfMonth },
      (_, day) => ({
        day: (day + 1).toString(),
        amount: monthlyAmounts[day + 1]?.toString() || "0",
      })
    );

    return formattedMonthlyData;
  };
  //////

  useEffect(() => {
    setMonthlyData(calculateMonthlyData(data));
    console.log(monthlyData);
  }, [data]);

  const sortedMonthlyData = monthlyData?.sort((a, b) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months.indexOf(a.month) - months.indexOf(b.month);
  });

  // Calculate the total sum of amounts
  const totalSum = sortedMonthlyData?.reduce((sum, entry) => {
    const finalSum = sum + parseFloat(entry.amount);
    return finalSum;
  }, 0);

  const formattedTotalSum = formatAmount(totalSum);

  function formatAmount(amount) {
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + "m";
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(1) + "k";
    }
    return amount.toString();
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dayNumber = parseInt(label);
      const date = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        dayNumber
      );
      const formattedDate = `${dayNumber}${
        ["th", "st", "nd", "rd"][
          (dayNumber % 100 >> 3) ^ 1 && dayNumber % 10
        ] || "th"
      } ${date.toLocaleDateString("en-US", {
        month: "short",
      })}`;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        >
          <p className="label">{`${formattedDate}`}</p>
          <p className="label">{`amount : $${payload[0].value}`}</p>
        </div>
      );
    }

    return null;
  };

  // Calculate the maximum amount, let it be the highest YAxis value
  const maxAmount = Math.max(
    ...monthlyData.map((entry) => parseFloat(entry.amount))
  );

  const noDataStyles = {
    p: "10px",
    borderRadius: "5px",
    boxShadow: "3px 8px 13px 0px #8080800f",
    bg: noDataBgColor,
    w: "100%",
    fontSize: ".9em",
  };

  return (
    <>
      {formattedTotalSum > 0 ? (
        <Stack direction={"row"} mb="20px" justifyContent={"space-between"}>
          <Stack direction={"column"} gap={20}>
            <Heading fontSize="1.5em">Monthly Earnings</Heading>

            <Heading>${formattedTotalSum}</Heading>
          </Stack>

          <ResponsiveContainer width="100%" height={400}>
            {monthlyData?.length > 0 ? (
              <BarChart
                data={monthlyData}
                margin={{
                  top: 10,
                  left: 20,
                  right: 20,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray="3 3"></CartesianGrid>
                <XAxis dataKey="day" />
                <YAxis domain={[0, maxAmount]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="amount" fill="#4f46ba" />
              </BarChart>
            ) : (
              <p>Loading...</p>
            )}
          </ResponsiveContainer>
        </Stack>
      ) : (
        <Stack
          direction={"column"}
          mb="20px"
          justifyContent={"center"}
          {...noDataStyles}
        >
          <Heading fontSize="1.5em">Monthly Earnings</Heading>
          <Text textAlign={"center"}>No data at the moment</Text>
        </Stack>
      )}
    </>
  );
};

export default MonthlyEarningChart;
